import SDKConfigurationModel from "../../../Configurator/Models/SDKConfiguraitonModel";
import ClientHttps from "../../../Https/ClientHttps";
import Loyalty from "../../Loyalty/Loyalty";
import MatchQuiz from "../../MatchQuiz/MatchQuiz";
import MiniGames from "../../MiniGames/MiniGames";
import TopX from "../../TopX/TopX";
import Voting from "../../Voting/Voting";
import ListContentFilters from "../Models/Filters/ListContentFilters";
import ListContentModel, { ListEntityType } from "../Models/ListContentModel";

export default class ListsFacade {
  private clientHttps: ClientHttps = null;
  private topX: TopX = null;
  private matchQuiz: MatchQuiz = null;
  private loyalty: Loyalty = null;
  private miniGames: MiniGames = null;
  private voting: Voting = null;

  constructor(config: SDKConfigurationModel) {
    this.clientHttps = new ClientHttps(config);
    this.topX = new TopX(config);
    this.matchQuiz = new MatchQuiz(config);
    this.loyalty = new Loyalty(config);
    this.miniGames = new MiniGames(config);
    this.voting = new Voting(config);
  }

  public getListContent = async (
    listId: string,
    filters: ListContentFilters,
    disableCache: boolean
  ) => {
    if (filters) {
      filters = new ListContentFilters(filters);
    }

    const response = await this.clientHttps.getListContent(
      listId,
      filters,
      disableCache
    );
    await this.setModels(response.data, disableCache);

    return response;
  };

  private setModels = async (
    data: ListContentModel[],
    disableCache: boolean
  ) => {
    let models: any[] = [];

    // Reconstruct the array to a map where the key is the entity type and the value is array of ids
    const map = new Map<ListEntityType, string[]>();

    data.forEach((v) => {
      if (map.has(v.entityType)) {
        const currentIds = map.get(v.entityType) as string[];
        map.set(v.entityType, [...currentIds, v.entityId]);
      } else {
        map.set(v.entityType, [v.entityId]);
      }
    });

    try {
      for (const [entityType, ids] of Array.from(map.entries())) {
        switch (entityType) {
          case "TOP_X":
            const topX = await this.topX.getGames(
              { limit: ids.length, gameIds: ids },
              disableCache
            );
            models = [...models, ...topX.data];
            break;
          case "MATCH_QUIZ":
            const matchQuizzes = await this.matchQuiz.getGames(
              { limit: ids.length, gameIds: ids },
              disableCache
            );
            models = [...models, ...matchQuizzes.data];
            break;
          case "LEADERBOARD_TEMPLATES":
            const templates = await this.loyalty.getTemplates(
              undefined,
              disableCache
            );
            models = [
              ...models,
              ...templates.data.filter((d) => ids.includes(d.id)),
            ];
            break;
          case "CLASSIC_QUIZ":
            const classicQuizzes = await this.miniGames.getClassicQuizzes(
              { limit: ids.length, classicQuizIds: ids },
              false,
              disableCache
            );
            models = [...models, ...classicQuizzes.data];
            break;
          case "EITHER_OR":
            const eitherOrs = await this.miniGames.getEitherOrs(
              { limit: ids.length, eitherOrIds: ids },
              false,
              disableCache
            );
            models = [...models, ...eitherOrs.data];
            break;
          case "POLL":
            const polls = await this.voting.getPolls(
              { limit: ids.length, pollIds: ids },
              disableCache
            );
            models = [...models, ...polls.data];
            break;
          case "PERSONALITY_QUIZ":
            const personalityQuizzes = await this.miniGames.getPersonalityQuizzes(
              { limit: ids.length, personalityQuizIds: ids },
              disableCache
            );
            models = [...models, ...personalityQuizzes.data];
            break;
          default:
        }
      }
    } catch (error) {
      console.error(
        "An error occurred while trying to set models for list content entities",
        error
      );
      throw error;
    }

    data.forEach((v) => {
      const model = models.find((m) => m.id === v.entityId) || null;
      v.entityModel = model;
    });
  };
}
